import { createContext, useState } from "react";
import {
  GapByCountyStatusBreakdown,
  NetworkPortfolioManagementMembership,
  NetworkPortfolioManagementProject,
} from "@app/network-portfolio-management/interfaces";
import { ProjectRunConfigurationType } from "@app/projects/enumerators";

export interface UseMembershipToggleResult {
  active: NetworkPortfolioManagementMembership | null;
  membership: ProjectRunConfigurationType;
  setMembership: (value: ProjectRunConfigurationType) => void;
  showMembershipToggle: boolean;
  /**
   * Gap by County Status for the currently-selected membership — populated only by
   * ProjectTableRow, which has access to the per-page-batched breakdown (see
   * useGapByCountyStatusByPage); useMembershipToggle itself has no access to it, so it's always
   * undefined when this hook is called directly (e.g. by ProjectCard).
   */
  gapByCountyStatus?: GapByCountyStatusBreakdown | null;
  gapByCountyStatusHasError?: boolean;
  isGapByCountyStatusLoading?: boolean;
}

export interface MembershipToggleOverride {
  membership: ProjectRunConfigurationType;
  setMembership: (value: ProjectRunConfigurationType) => void;
}

/**
 * Primary/secondary membership toggle for dual-membership NPM projects. Shared by ProjectCard and
 * the tabular view's Dual Membership column.
 *
 * `override` lets a caller supply externally-owned state instead of the internal `useState` —
 * needed by the virtualized table view, where TanStack Virtual unmounts/remounts
 * ProjectTableRow as rows scroll in and out of the overscan window; local useState would reset
 * to the default on every remount. ProjectCard stays mounted for the lifetime of its list, so it
 * doesn't pass one.
 */
export const useMembershipToggle = (
  project: NetworkPortfolioManagementProject,
  override?: MembershipToggleOverride,
): UseMembershipToggleResult => {
  const isArchived = project.dateArchived != null;
  const [localMembership, setLocalMembership] = useState<ProjectRunConfigurationType>(
    ProjectRunConfigurationType.DualMembershipPrimary,
  );
  const membership = override?.membership ?? localMembership;
  const setMembership = override?.setMembership ?? setLocalMembership;
  const showMembershipToggle = !isArchived && project.isDualMembership && project.secondary != null;
  const active =
    showMembershipToggle && membership === ProjectRunConfigurationType.DualMembershipSupplemental
      ? project.secondary
      : project.primary;

  return { active, membership, setMembership, showMembershipToggle };
};

const defaultMembershipToggleValue: UseMembershipToggleResult = {
  active: null,
  membership: ProjectRunConfigurationType.DualMembershipPrimary,
  setMembership: () => {},
  showMembershipToggle: false,
};

/**
 * Distributes one row's membership-toggle state to table cells that render as independent
 * TanStack columns (Dual Membership, Adequacy Index Score, Accuracy Index Score) so they read a
 * single shared state instead of each calling useMembershipToggle separately and drifting out of
 * sync. ProjectTableRow.tsx provides this once per row; ProjectCard.tsx doesn't need it since it
 * calls the hook directly at its own top level.
 */
export const MembershipToggleContext = createContext<UseMembershipToggleResult>(defaultMembershipToggleValue);
